import axios from 'axios'
import { ElMessage } from 'element-plus'
import i18n from '@/locale'
import { pushDebugInfo, setToken } from '../utils'
import { isByteStream } from '@/libs/utils'
import { appStore, companyStore, userStore } from '@/stores'
import { useAppStore } from '@/stores/modules/appStore'
import { logOutSuccess } from '@/services/login'

const { t } = i18n.global
const env = import.meta.env

// 显示中的错误提示: {'Please login first': true}
// 防止错误信息的重复显示，同一个错误信息显示2500ms后自动清除
const errMsgData = {}
class HttpRequest {
  constructor() {
    this.queue = {}
  }

  getInsideConfig() {
    const config = {
      baseURL: this.getApiPrefix(),
      timeout: 30000,
      headers: {
        'x-token': userStore.token,
        'Accept-Language': appStore.language,
        'Content-Type': 'application/json',
      },
    }
    if (this.options?.apiType === 'app') {
      config.headers.Authorization = env.VITE_API_APP_AUTH
      config.headers['Product-Name'] = 'UPDF'
      config.headers['Device-Type'] = 'WEB'
    }
    return config
  }

  /**
   * 获取请求域名前缀
   * @returns string
   */
  getApiPrefix() {
    const appStore = useAppStore()
    const apiPrefixData = {
      account: appStore.isCN ? env.VITE_API_PREFIX_CN : env.VITE_API_PREFIX_EN,
      app: appStore.isCN ? env.VITE_API_PREFIX_APP_CN : env.VITE_API_PREFIX_APP_EN,
      checkout: appStore.isCN ? env.VITE_API_PREFIX_CHECKOUT_CN : env.VITE_API_PREFIX_CHECKOUT_EN,
    }
    const apiPrefixType = this.options?.apiType || 'account'
    let apiPrefix = apiPrefixData[apiPrefixType]
    return apiPrefix
  }

  destroy(url) {
    delete this.queue[url]
  }

  interceptors(instance, url) {
    // 请求拦截
    instance.interceptors.request.use(config => {
      this.queue[url] = true
      return config
    })
    // 响应拦截

    instance.interceptors.response.use(
      res => {
        // 添加调试信息
        const debugInfo = {
          url,
          res,
        }
        pushDebugInfo(debugInfo)
        const headers = res.headers
        // 如果token快过期了，会返回一个新的token用作自动续期
        if (headers['x-new-token'] && headers['x-new-expires-at']) {
          setToken(headers['x-new-token'], headers['x-new-expires-at'])
        }
        // 可以在定义接口时增加showError=false来不让自动显示错误信息
        // eslint-disable-next-line no-eq-null
        const showError = res.config.showError || res.config.showError == null
        const data = res.data
        this.destroy(url)
        // 企业后台可以出现valid token的情况，进行拦截和登出操作
        if ([401, 2008, 2006, 6401, 6006, 2412].includes(data.code)) {
          companyStore.hasPermission = false
          if (showError) this.showError(data)
          setTimeout(() => {
            logOutSuccess()
          }, 1000)
          return Promise.reject(data)
        }

        //  权限许可
        if (data.code === 403) {
          companyStore.hasPermission = false
        }
        // 201时导出excel的解口返回的
        if (data.code === 200 || isByteStream(data)) {
          return data
        } else {
          if (showError) this.showError(data)
          return Promise.reject(data)
        }
      },
      error => {
        // 添加调试信息
        const debugInfo = {
          url,
          error,
        }
        pushDebugInfo(debugInfo)
        console.log(error.message, '---message')
        if (error.message.includes('timeout')) {
          error.code = 408
          // eslint-disable-next-line no-eq-null
          if (error.config.showError || error.config.showError == null) this.showError(error)
          // 如果遇到超时请求，则后续请求禁用zga访问
          appStore.useZgaApi = true
        }
        const res = { code: 408, data: {}, msg: 'error' }
        return Promise.reject(res)
      }
    )
  }

  /**
   * 显示错误信息，防止相同的错误信息重复显示，2.5秒后自动清除，清除后相同的错误信息会再次显示
   * @param {*} errMsg
   */
  showError(err) {
    let errMsg = err.msg || err.message
    const codeMsgKey = `http_error_${err.code}`
    // 如果文案和codeKey相等，说明没有配置相关code对应的文案，则直接显示后端返回的错误信息
    if (t(codeMsgKey) !== codeMsgKey) {
      errMsg = t(codeMsgKey)
    }
    // 相同的错误信息同一时间内不重复显示
    if (!errMsgData[errMsg]) {
      ElMessage.error({
        message: errMsg,
        duration: 3000,
      })
      errMsgData[errMsg] = true
      setTimeout(() => {
        delete errMsgData[errMsg]
      }, 2500)
    }
  }

  request(options) {
    options.apiType = options.apiType || 'account'
    // token失效是否自动跳转到登录页，默认自动跳转
    options.gotoLoginWhenTokenError = options.gotoLoginWhenTokenError === undefined ? true : options.gotoLoginWhenTokenError
    this.options = options
    const instance = axios.create()
    const config = this.getInsideConfig()
    if (options.headers) {
      config.headers = Object.assign({}, config.headers, options.headers)
      delete options.headers
    }
    options = Object.assign({}, config, options)
    this.interceptors(instance, options.url)
    return instance(options)
  }
}

export default HttpRequest
